import { useEffect, useMemo, useState } from 'react';
import type { Product } from '../types/api';
import ProductImage from './ProductImage';
import { ArrowRightIcon, ChipIcon, FlashIcon, PackageIcon, ShoppingCartIcon, ShieldIcon, WrenchIcon } from './Icons';
import './Banner.css';

interface Props {
  featuredProducts: Product[];
  onExploreCatalog: () => void;
  onQuickOrder: () => void;
}

function hasRealImage(product: Product): boolean {
  return Boolean(product.image) && !product.image.includes('product-placeholder.svg');
}

type Slide = {
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  tag: string;
  badge: string;
  accent: string;
};

const SLIDES: Slide[] = [
  {
    eyebrow: 'Ofertas da semana',
    title: 'HORTIFRUTI FRESCO',
    highlight: 'Segunda e quinta',
    description: 'Frutas, verduras e legumes com giro diário para chamar o cliente pro carrinho.',
    tag: 'hortifruti',
    badge: 'Fresco todo dia',
    accent: 'economia',
  },
  {
    eyebrow: 'Fim de semana',
    title: 'CHURRASCO E GELADA',
    highlight: 'Sextou de verdade',
    description: 'Carnes, cerveja, carvão e acompanhamentos para o movimento da loja subir.',
    tag: 'churrasco',
    badge: 'Promo imperdível',
    accent: 'fim de semana',
  },
  {
    eyebrow: 'Promoção forte',
    title: 'PREÇO BAIXO NO CARRINHO',
    highlight: 'Mercearia e limpeza',
    description: 'Oferta comercial para itens de giro, com destaque visual e compra rápida.',
    tag: 'ofertas',
    badge: 'Preço baixo',
    accent: 'oferta',
  },
  {
    eyebrow: 'Datas especiais',
    title: 'OFERTAS SAZONAIS',
    highlight: 'Natal, Páscoa e festas',
    description: 'Campanhas por mês e datas comemorativas com banner trocando conforme a campanha.',
    tag: 'campanhas',
    badge: 'Campanha do mês',
    accent: 'sazonal',
  },
];

export default function Banner({ featuredProducts, onExploreCatalog, onQuickOrder }: Props) {
  const [slideIndex, setSlideIndex] = useState(0);

  const slides = useMemo(() => SLIDES, []);
  const slide = slides[slideIndex % slides.length];
  const visualProducts = useMemo(() => {
    const rotated = featuredProducts.filter(hasRealImage);
    if (rotated.length === 0) return [];
    const start = slideIndex % rotated.length;
    return rotated.slice(start, start + 4).concat(rotated.slice(0, Math.max(0, 4 - (rotated.length - start)))).slice(0, 4);
  }, [featuredProducts, slideIndex]);
  const hasVisualProducts = visualProducts.length > 0;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSlideIndex(prev => (prev + 1) % slides.length);
    }, 7000);

    return () => window.clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="banner section-shell" aria-label="Banner principal Villamix Express">
      <div className="banner-content">
        <div className="banner-copy banner-copy--commercial">
          <span className="banner-eyebrow">
            <PackageIcon size={16} />
            {slide.eyebrow}
          </span>

          <h1>
            <span>OFERTAS</span>
            <span>DA SEMANA</span>
          </h1>

          <h2>{slide.title}</h2>

          <p>{slide.description}</p>

          <div className="banner-promo-pill">
            <FlashIcon size={16} />
            <strong>{slide.highlight}</strong>
            <span>{slide.badge}</span>
          </div>

          <div className="banner-actions">
            <button className="banner-cta banner-cta--primary" onClick={onExploreCatalog} type="button">
              <span>Ver ofertas</span>
              <ArrowRightIcon size={18} />
            </button>

            <button className="banner-cta banner-cta--secondary" onClick={onQuickOrder} type="button">
              <FlashIcon size={18} />
              <span>Comprar agora</span>
            </button>
          </div>

          <div className="banner-trust" aria-label="Indicadores de confianca">
            <span><ShieldIcon size={15} /> Compra segura</span>
            <span><PackageIcon size={15} /> Estoque atualizado</span>
            <span><WrenchIcon size={15} /> {slide.accent}</span>
          </div>

          <div className="banner-promo-dots" aria-label="Alternar campanhas">
            {slides.map((item, index) => (
              <button
                key={item.tag}
                type="button"
                className={index === slideIndex ? 'active' : ''}
                onClick={() => setSlideIndex(index)}
                aria-label={`Ver campanha ${item.tag}`}
              />
            ))}
          </div>
        </div>

        <div className="banner-visual" aria-hidden={!hasVisualProducts}>
          {hasVisualProducts ? (
            <>
              <div className="banner-visual-badge">
                <span>{slide.badge}</span>
                <strong>{slide.highlight}</strong>
              </div>
              <div className="banner-visual-glow" />
              <div className="banner-visual-products">
                {visualProducts.map((product, index) => (
                  <div key={product.id} className={`banner-product-card banner-product-card--${index + 1}`}>
                    <ProductImage src={product.image} alt="" className="banner-product-image" />
                  </div>
                ))}
              </div>
              <div className="banner-visual-tags">
                <span>{slide.tag}</span>
                <span>Variedade de produtos</span>
                <span>Compra agil</span>
              </div>
            </>
          ) : (
            <div className="banner-fallback" aria-hidden="true">
              <div className="banner-visual-badge">
                <span>{slide.badge}</span>
                <strong>{slide.highlight}</strong>
              </div>
              <div className="banner-fallback-glow" />
              <div className="banner-fallback-card banner-fallback-card--main">
                <ChipIcon size={30} />
                <span>{slide.tag}</span>
              </div>
              <div className="banner-fallback-card banner-fallback-card--secondary">
                <WrenchIcon size={28} />
                <span>Promo do dia</span>
              </div>
              <div className="banner-fallback-card banner-fallback-card--accent">
                <ShoppingCartIcon size={26} />
                <span>{slide.accent}</span>
              </div>
              <div className="banner-visual-tags">
                <span>{slide.eyebrow}</span>
                <span>Variedade de produtos</span>
                <span>Compra agil</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
